import { useEffect, useRef } from "react";
import { Bars, Brand, Heading, Kicker, MetricGrid, Section } from "./components";
import {
  reportMediaKitImageLoad,
  reportMediaKitImageOpen,
} from "./analyticsRuntime";
import { cloudinarySrcSet, cloudinaryUrl, type ImageOptions } from "./cloudinary";
import { PhoneVideoMockup } from "./PhoneVideoMockup";
import type { MediaKitContent } from "./types";

/** Mux smart-crop poster when CMS has no Cloudinary poster */
function muxPosterUrl(playbackId: string): string {
  const id = playbackId.trim();
  if (!id) return "";
  return `https://image.mux.com/${encodeURIComponent(id)}/thumbnail.jpg?time=1&width=720&height=1280&fit_mode=smartcrop`;
}

const sortByOrder = <T extends { sortOrder: number }>(items: T[]) =>
  [...items].sort((a, b) => a.sortOrder - b.sortOrder);

function personName(content: MediaKitContent): string {
  const n = `${content.profile.name} ${content.profile.surname}`.trim();
  return n || "Delfu Ocampo";
}

type ImageVariant = "hero" | "portrait" | "gallery" | "card";

/** Quality-first presets — avoid hard crop/crush for brand media kits */
const VARIANT: Record<ImageVariant, ImageOptions & { widths: number[] }> = {
  hero: {
    width: 1920,
    quality: "auto:good",
    format: "auto",
    crop: "fill",
    gravity: "auto",
    dprAuto: true,
    widths: [640, 960, 1280, 1600, 2000],
  },
  portrait: {
    width: 1200,
    quality: "auto:good",
    format: "auto",
    crop: "fill",
    gravity: "face",
    dprAuto: true,
    widths: [480, 720, 960, 1200, 1600],
  },
  gallery: {
    width: 1400,
    quality: "auto:good",
    format: "auto",
    crop: "limit",
    dprAuto: true,
    widths: [480, 768, 1080, 1440, 1800],
  },
  card: {
    width: 1000,
    quality: "auto:good",
    format: "auto",
    crop: "fill",
    gravity: "auto",
    dprAuto: true,
    widths: [400, 640, 900, 1200],
  },
};

function CmsImage({
  image,
  className,
  sizes,
  priority = false,
  variant = "gallery",
  role,
}: {
  image: MediaKitContent["hero"]["image"];
  className?: string;
  sizes?: string;
  priority?: boolean;
  variant?: ImageVariant;
  /** Semantic role for analytics: hero | portrait | portfolio | concept */
  role?: string;
}) {
  const preset = VARIANT[variant];
  const { widths, ...opts } = preset;
  const src = cloudinaryUrl(image, opts);
  const srcSet = cloudinarySrcSet(image, widths, {
    quality: opts.quality,
    format: opts.format,
    crop: opts.crop,
    gravity: opts.gravity,
  });
  const imgRef = useRef<HTMLImageElement>(null);
  const loadStarted = useRef(performance.now());
  const reported = useRef(false);

  useEffect(() => {
    loadStarted.current = performance.now();
    reported.current = false;
    const el = imgRef.current;
    // Cached images may already be complete without firing onLoad
    if (el?.complete && el.naturalWidth > 0 && !reported.current) {
      reported.current = true;
      reportMediaKitImageLoad(
        {
          publicId: image.publicId,
          variant,
          role: role || variant,
          alt: image.alt,
          loadStartedAt: loadStarted.current,
        },
        el,
        true,
      );
    }
  }, [image.publicId, variant, role, image.alt, src]);

  return (
    <img
      ref={imgRef}
      className={className}
      src={src}
      srcSet={srcSet}
      sizes={sizes}
      alt={image.alt}
      width={image.width}
      height={image.height}
      loading={priority ? "eager" : "lazy"}
      decoding="async"
      data-mk-img="1"
      data-mk-public-id={image.publicId}
      data-mk-variant={variant}
      data-mk-role={role || variant}
      data-mk-alt={image.alt || ""}
      data-photo-track
      data-photo-src={src}
      // Click / tap = “open” for /admin/fotos-analytics (quién abrió + país)
      onClick={() => {
        reportMediaKitImageOpen({
          publicId: image.publicId,
          variant,
          role: role || variant,
          alt: image.alt,
          src,
        });
      }}
      style={{ cursor: "pointer" }}
      onLoad={(e) => {
        if (reported.current) return;
        reported.current = true;
        reportMediaKitImageLoad(
          {
            publicId: image.publicId,
            variant,
            role: role || variant,
            alt: image.alt,
            loadStartedAt: loadStarted.current,
          },
          e.currentTarget,
          true,
        );
      }}
      onError={(e) => {
        if (reported.current) return;
        reported.current = true;
        reportMediaKitImageLoad(
          {
            publicId: image.publicId,
            variant,
            role: role || variant,
            alt: image.alt,
            loadStartedAt: loadStarted.current,
          },
          e.currentTarget,
          false,
          "img_error",
        );
      }}
      {...(priority ? { fetchPriority: "high" as const } : {})}
    />
  );
}

export function HeroSection({ content }: { content: MediaKitContent }) {
  const { profile, hero, brand } = content;
  return (
    <Section id="portada" page="01" className="mk-hero" footerName={personName(content)}>
      <CmsImage
        className="mk-hero__image"
        image={hero.image}
        variant="hero"
        role="hero"
        sizes="100vw"
        priority
      />
      <div className="mk-hero__veil" />
      <header className="mk-hero__top">
        <Kicker>
          <span className="mk-live-dot" /> Media Kit
        </Kicker>
        <Brand brand={brand} />
      </header>
      <div className="mk-hero__content">
        <Kicker>{hero.kicker}</Kicker>
        <h1>
          {profile.name} <em>{profile.surname}</em>
        </h1>
        <div className="mk-hero__meta">
          <p>{profile.descriptor}</p>
          <div>
            <a
              href={profile.instagram.href}
              target={profile.instagram.newTab ? "_blank" : undefined}
              rel="noreferrer"
            >
              {profile.instagram.label}
            </a>
            <span>
              {profile.location} · {profile.age}
            </span>
          </div>
        </div>
      </div>
    </Section>
  );
}

export function AboutSection({ content }: { content: MediaKitContent }) {
  const { about, performance } = content;
  return (
    <Section id="perfil" page="02" footerName={personName(content)}>
      <Kicker>{about.eyebrow}</Kicker>
      <Heading accent={about.headingAccent}>{about.heading}</Heading>
      <div className="mk-profile-layout">
        <div className="mk-profile-copy">
          {about.paragraphs.map((paragraph) => (
            <p key={paragraph}>{paragraph}</p>
          ))}
          <div className="mk-chips">
            {about.tags.map((tag) => (
              <span key={tag}>{tag}</span>
            ))}
          </div>
          <MetricGrid items={sortByOrder(performance.headlineMetrics).slice(0, 2)} compact />
        </div>
        <figure className="mk-portrait">
          <CmsImage
            image={about.image}
            variant="portrait"
            role="portrait"
            sizes="(max-width: 840px) 100vw, 420px"
          />
        </figure>
      </div>
    </Section>
  );
}

export function PerformanceSection({ content }: { content: MediaKitContent }) {
  const { performance } = content;
  return (
    <Section id="metricas" page="03" footerName={personName(content)}>
      <Kicker>{performance.eyebrow}</Kicker>
      <Heading accent={performance.headingAccent}>{performance.heading}</Heading>
      {performance.periodLabel ? (
        <p className="mk-period-label">{performance.periodLabel}</p>
      ) : null}
      <MetricGrid items={sortByOrder(performance.headlineMetrics)} />
      <p className="mk-platforms-label">Por red social</p>
      <div className="mk-platforms">
        {sortByOrder(performance.platformMetrics).map((metric) => (
          <article key={metric.id}>
            <strong className={`mk-tone-${metric.tone}`}>{metric.value}</strong>
            <span>{metric.label}</span>
            {metric.hint ? <em className="mk-metric-hint">{metric.hint}</em> : null}
          </article>
        ))}
      </div>
      <aside className="mk-note">
        <Kicker>{performance.noteTitle}</Kicker>
        <p>{performance.noteBody}</p>
      </aside>
    </Section>
  );
}

export function AudienceSection({ content }: { content: MediaKitContent }) {
  const { audience } = content;
  return (
    <Section id="audiencia" page="04" footerName={personName(content)}>
      <Kicker>{audience.eyebrow}</Kicker>
      <Heading accent={audience.headingAccent}>{audience.heading}</Heading>
      <p className="mk-lede">{audience.intro}</p>
      <div className="mk-audience-grid">
        <Bars title="Edad" rows={sortByOrder(audience.age)} />
        <div className="mk-gender-card">
          {sortByOrder(audience.gender).map((item) => (
            <div key={item.id}>
              <strong className={`mk-tone-${item.tone ?? "gold"}`}>{item.value}</strong>
              <span>{item.label}</span>
            </div>
          ))}
        </div>
        <Bars
          title="Países (top)"
          rows={sortByOrder(audience.countries)}
          showCountryFlags
        />
        <div className="mk-interest-card">
          <h3>Intereses de la audiencia</h3>
          {audience.interests.map((item) => (
            <span key={item}>{item}</span>
          ))}
        </div>
      </div>
    </Section>
  );
}

export function VisualWorldSection({ content }: { content: MediaKitContent }) {
  const { visualWorld } = content;
  return (
    <Section id="mundo" page="05" footerName={personName(content)}>
      <Kicker>{visualWorld.eyebrow}</Kicker>
      <Heading accent={visualWorld.headingAccent}>{visualWorld.heading}</Heading>
      <div className="mk-gallery">
        {sortByOrder(visualWorld.images).map((item) => (
          <figure key={item.id}>
            <CmsImage
              image={item}
              variant="gallery"
              role="portfolio"
              sizes="(max-width: 840px) 50vw, 420px"
            />
            <figcaption>{item.caption}</figcaption>
          </figure>
        ))}
      </div>
    </Section>
  );
}

export function CampaignsSection({ content }: { content: MediaKitContent }) {
  const { campaigns } = content;
  return (
    <Section id="conceptos" page="06" footerName={personName(content)}>
      <Kicker>{campaigns.eyebrow}</Kicker>
      <Heading accent={campaigns.headingAccent}>{campaigns.heading}</Heading>
      <p className="mk-lede">{campaigns.intro}</p>
      <div className="mk-concept-grid">
        {sortByOrder(campaigns.concepts).map((concept) => (
          <article className="mk-concept-card" key={concept.id}>
            <div className="mk-concept-card__image">
              <CmsImage
                image={concept.image}
                variant="card"
                role="concept"
                sizes="(max-width: 840px) 100vw, 400px"
              />
              <span>{concept.eyebrow}</span>
            </div>
            <div className="mk-concept-card__body">
              <h3>{concept.title}</h3>
              <p>{concept.copy}</p>
              <div>
                {concept.tags.map((tag) => (
                  <small key={tag}>{tag}</small>
                ))}
              </div>
            </div>
          </article>
        ))}
      </div>
    </Section>
  );
}

/**
 * Optional extra: iPhone mockup + Mux (or MP4). Admin enables + reorders in layout.
 * Mobile: copy → phone. Desktop: split copy | phone (or mirrored).
 */
export function PhonePreviewSection({ content }: { content: MediaKitContent }) {
  const pp = content.phonePreview;
  if (!pp) return null;

  const playbackId = pp.muxPlaybackId?.trim() ?? "";
  const fallback = pp.videoSrcFallback?.trim() ?? "";
  const hasMedia = Boolean(playbackId || fallback);

  const posterFromCms =
    pp.posterImage?.publicId
      ? cloudinaryUrl(pp.posterImage, {
          width: 720,
          quality: "auto:good",
          format: "auto",
          crop: "fill",
          gravity: "auto",
        })
      : "";
  const poster =
    posterFromCms || (playbackId ? muxPosterUrl(playbackId) : undefined);

  const layoutDesktop = pp.layoutDesktop === "copyRight" ? "copyRight" : "copyLeft";
  const alignPhone = pp.alignPhone === "center" ? "center" : "end";
  const bullets = Array.isArray(pp.bullets)
    ? pp.bullets.map((b) => b.trim()).filter(Boolean).slice(0, 4)
    : [];
  const ctaLabel = pp.ctaLabel?.trim() ?? "";
  const ctaHref = pp.ctaHref?.trim() ?? "";

  // Without media, skip public render (admin validation should block enable)
  if (!hasMedia) return null;

  return (
    <Section
      id="preview"
      page="—"
      className={`mk-phone-preview mk-phone-preview--${layoutDesktop} mk-phone-preview--phone-${alignPhone}`}
      footerName={personName(content)}
    >
      <div className="mk-phone-preview__grid">
        <div className="mk-phone-preview__copy">
          <Kicker>{pp.eyebrow}</Kicker>
          <Heading accent={pp.headingAccent}>{pp.heading}</Heading>
          {pp.body ? <p className="mk-lede">{pp.body}</p> : null}
          {bullets.length > 0 ? (
            <ul className="mk-phone-preview__bullets">
              {bullets.map((b) => (
                <li key={b}>
                  <span className="mk-phone-preview__bullet-mark" aria-hidden />
                  {b}
                </li>
              ))}
            </ul>
          ) : null}
          {ctaLabel && ctaHref ? (
            <div className="mk-phone-preview__cta">
              <a
                className="mk-button mk-button--primary"
                href={ctaHref}
                {...(ctaHref.startsWith("http")
                  ? { target: "_blank", rel: "noreferrer" }
                  : {})}
              >
                {ctaLabel}
              </a>
            </div>
          ) : null}
        </div>
        <div className="mk-phone-preview__device">
          <PhoneVideoMockup
            playbackId={playbackId || undefined}
            src={fallback || undefined}
            poster={poster || undefined}
            ariaLabel={`Video de ${personName(content)} en un iPhone`}
          />
        </div>
      </div>
    </Section>
  );
}

/** Deliverables brands can brief against (Reels, UGC, Stories, rights…) */
export function CollaborationSection({ content }: { content: MediaKitContent }) {
  const { collaboration } = content;
  if (!collaboration?.formats?.length) return null;
  return (
    <Section id="formatos" page="07" footerName={personName(content)}>
      <Kicker>{collaboration.eyebrow}</Kicker>
      <Heading accent={collaboration.headingAccent}>{collaboration.heading}</Heading>
      <p className="mk-lede">{collaboration.intro}</p>
      <div className="mk-deliverable-grid">
        {sortByOrder(collaboration.formats).map((fmt) => (
          <article className="mk-deliverable" key={fmt.id}>
            <h3>{fmt.title}</h3>
            <p>{fmt.description}</p>
            <div className="mk-deliverable__tags">
              {fmt.tags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          </article>
        ))}
      </div>
      {collaboration.notes?.length ? (
        <ul className="mk-collab-notes">
          {collaboration.notes.map((note) => (
            <li key={note}>{note}</li>
          ))}
        </ul>
      ) : null}
    </Section>
  );
}

export function ContactSection({ content }: { content: MediaKitContent }) {
  const { contact, profile, brand } = content;
  return (
    <Section id="contacto" page="08" className="mk-contact" footerName={personName(content)}>
      <div className="mk-contact__inner">
        <Kicker>{contact.eyebrow}</Kicker>
        <Heading accent={contact.headingAccent}>{contact.heading}</Heading>
        <p>{contact.body}</p>
        <div className="mk-contact__actions">
          <a className="mk-button mk-button--primary" href={profile.email.href}>
            Pedir rate card / enviar brief
          </a>
          <a
            className="mk-button"
            href={profile.instagram.href}
            target="_blank"
            rel="noreferrer"
          >
            {profile.instagram.label}
          </a>
        </div>
      </div>
      <div className="mk-contact__brand">
        <Brand brand={brand} />
        <span>Media kit · 2026</span>
      </div>
    </Section>
  );
}
